import { useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";

import { type Conversation } from "shared/apiClient";
import { getText, type Noticeboard } from "shared/lib";
import { EventTypes } from "@/types/events";

import {
  useAssessmentLights,
  useIsApplicationRequirementsEnabled,
} from "shared/hooks";

import { Separator } from "shared/components";
import { PermitRequirements } from "@/components/analysisLayout/permitRequirements";
import { ApplicationRequirements } from "@/components/analysisLayout/applicationRequirements";
import { LotAndProposal } from "@/components/analysisLayout/lotAndProposal";
import { NextSteps } from "@/components/analysisLayout/nextSteps";
import { ReportWarningPanel } from "@/components/wizard/ReportWarningPanel";
import { PrintViewHeader } from "./PrintViewHeader";

export interface PrintViewProps {
  conversation: Conversation | undefined | null;
  isLoading?: boolean;
  messages: Conversation["messages"];
  noticeboardData: Noticeboard | undefined | null;
}

export function PrintView({
  conversation,
  isLoading,
  messages,
  noticeboardData,
}: PrintViewProps) {
  const componentRef = useRef(null);

  const assessmentLights = useAssessmentLights(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const isApplicationRequirementsEnabled = useIsApplicationRequirementsEnabled(
    conversation,
    "enquiry",
  );

  const print = useReactToPrint({
    content: () => componentRef.current,
    removeAfterPrint: true,
    documentTitle: getText("MyLot Report"),
    onBeforeGetContent: () => {},
    onPrintError: () => alert(getText("There was an error during printing")),
  });

  useEffect(() => {
    const handlePrint: EventListener = (_event) => {
      print();
    };

    document.addEventListener(EventTypes.PrintPublicReport, handlePrint);

    return () => {
      document.removeEventListener(EventTypes.PrintPublicReport, handlePrint);
    };
  }, [print]);

  return (
    <div className="text-textPrimary h-dvh w-screen">
      {isLoading && <div>{getText("Loading")}</div>}

      {!isLoading && (
        <section
          aria-label={getText("Public report")}
          className="flex grow flex-col gap-5 overflow-auto px-5 py-5"
          role="region"
        >
          <PrintViewHeader
            conversation={conversation}
            noticeboardData={noticeboardData}
          />

          <Separator orientation="horizontal" />

          <PermitRequirements
            conversation={conversation}
            messages={messages}
            noticeboardData={noticeboardData}
            printable
          />

          {isApplicationRequirementsEnabled ? (
            <>
              <Separator orientation="horizontal" />

              <ApplicationRequirements
                conversation={conversation}
                messages={messages}
                noticeboardData={noticeboardData}
                printable
              />
            </>
          ) : null}

          <Separator orientation="horizontal" />

          <LotAndProposal
            conversation={conversation}
            messages={messages}
            noticeboardData={noticeboardData}
            printable
          />

          <Separator orientation="horizontal" />

          <NextSteps
            assessmentLights={assessmentLights}
            conversation={conversation}
            messages={messages || []}
            noticeboardData={noticeboardData}
            printable
          />

          <Separator orientation="horizontal" />

          <ReportWarningPanel
            conversation={conversation}
            headingClassName="text-headinglg"
          />
        </section>
      )}
    </div>
  );
}
